import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import FilterChips from 'components/shared/FilterChips';
import FilterBar from 'components/shared/filters/FilterBar';
import FilterMultiSelect from 'components/shared/filters/FilterMultiSelect';
import { FilterSection } from 'components/shared/filters/FilterSlideout';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import SegmentedControl from 'components/shared/SegmentedControl';
import ControlledTable from 'components/shared/table/ControlledTable';
import Tabs, { TabType } from 'components/Tabs';
import { defaultFilterDropdownConfig, FILTER_TYPES, sortFilterDropdowns } from 'constants/filterConfigs';
import { NONE_OPTION } from 'constants/filterKeysConstants';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import { usePortalName } from 'hooks/usePortalConnections';
import useWorkflowCount from 'hooks/useWorkflowCount';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import useWorkflows from 'hooks/useWorkflows';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import Workflow from 'models/Workflow';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { GroupAccessMode, useInfiniteGroups } from 'services/api/group';
import { indexWorkflows, WorkflowsIndexParams } from 'services/api/workflows';
import {
  defaultDateRange,
  HistoryChip,
  PAGE_SIZE,
  serializeSort,
  StatusChip,
  Tab,
  useInboundReferralsActions,
  useInboundReferralsStore,
} from 'stores/inboundReferralsStore';
import { BarFilterState, FilterOption } from 'stores/types';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

import {
  OUTCOME_DENIED,
  OUTCOME_NOT_SELECTED,
  OUTCOME_SELECTED,
  REFERRAL_INTAKE_API_NAME,
  VALUE_NAMES,
} from './constants';
import HistoryDateRange from './HistoryDateRange';
import inboundReferralsTableColumns from './inboundReferralsTableColumns';
import ReferralModal from './ReferralModal';
import { LIST_STATUS_DISPLAY, ListStatus } from './referralProgress';
import { TONE_STYLES } from './StatusBadge';

// The app-standard filter shape FilterBar works in: a record of FilterValueArrays holding
// {id, name} options (what FilterMultiSelect reads and writes), a FilterValueBoolean per checkbox,
// plus the search term FilterBar carries alongside them. Same shape Portfolio passes, and the same
// shape FilterChips reads to draw the chip row.
type BarFilters = BarFilterState;

// What the query carries for a chosen option: the reporting name, except for "None", which carries
// the app's `null` sentinel (NONE_OPTION). The api reads that as "no resolved referral source"
// rather than looking for a hospital by that name.
const filterValueFor = (option: FilterOption) => (option.id === NONE_OPTION.id ? NONE_OPTION.id : (option.name ?? ''));

// Back the other way, so a re-opened slideout shows "None" ticked rather than the raw sentinel.
const filterOptionFor = (value: string) =>
  value === NONE_OPTION.id ? NONE_OPTION : ({ id: value, name: value } as FilterOption);

// The receiving-SNF filter is keyed on the group id, not a name (unlike Referral Source): the api
// matches workflow_groups.group_id, the same value the Insights facility filter sends. So the option
// value is the id, and the store holds ids. Reconstructing an option from a stored id only needs the
// id — the MultiSelect control shows a count, not each label, and the open menu re-labels a ticked
// option off the fetched group list, matched by id.
const snfValueFor = (option: FilterOption) => (option.id as string) ?? '';
const snfOptionFor = (id: string) => ({ id }) as FilterOption;

// Legend swatches — only the four surfaced states (pending/error aren't shown).
const LEGEND: ListStatus[] = ['actionNeeded', 'processing', 'readyForReview', 'complete'];

// What each tab asks the api for, before chips, ranges, search, and the slideout's filters. The three
// partition the list (OLIO-3176): Awaiting Admission is the referrals whose Selected outcome queued a
// patient not yet admitted — whatever the run's status — and the other two subtract it. A record, not
// a ternary, so a new tab cannot fall into another tab's params by default.
//
// History asks `history` rather than listing the terminal statuses (OLIO-3470). Membership is no longer
// run status alone: a Selected referral whose patient arrived belongs on History even if its run failed
// to close, which is how referrals used to strand on Active with the patient already in a bed. The
// predicate lives on the api so the list and the chip counts cannot drift from each other.
//
// History still sends `awaitingAdmission: false`. The two overlap on a terminal Selected referral whose
// patient never arrived, and Awaiting Admission has to win that row — it is an unresolved placement, not
// a finished one.
const TAB_PARAMS: Record<Tab, Partial<WorkflowsIndexParams>> = {
  active: { history: false, awaitingAdmission: false },
  awaitingAdmission: { awaitingAdmission: true },
  history: { history: true, awaitingAdmission: false },
};

function tabParams(tab: Tab): Partial<WorkflowsIndexParams> {
  return TAB_PARAMS[tab];
}

// The chip row (Active tab only). Each chip selects the Current Status pills it is named for, and
// nothing the table doesn't show (a product call). The api computes list_status as the viewer's own
// pill: their buildings' steps, with a recorded outcome or an all-denied read first. So `listStatus`
// is the whole filter. `error` rides under Flagged because no other chip carries a broken run.
//
// Not `queue`: that is a per-building "needs a person" flag the table never renders, so pairing it
// dropped rows whose pill read Flagged.
function chipParams(chip: StatusChip): Partial<WorkflowsIndexParams> {
  if (chip === 'actionNeeded') return { listStatus: ['action_needed', 'error'] };
  if (chip === 'readyForReview') return { listStatus: 'ready_for_review' };
  return {};
}

// The History tab's chip row (OLIO-3295): the recorded outcome, on the denormalized `outcome`
// column (list_status deliberately never matches a finished run). 'all' adds nothing — every
// concluded referral the tab's other filters allow.
const HISTORY_CHIP_OUTCOME: Record<HistoryChip, string | undefined> = {
  all: undefined,
  selected: OUTCOME_SELECTED,
  notSelected: OUTCOME_NOT_SELECTED,
  denied: OUTCOME_DENIED,
};

function historyChipParams(chip: HistoryChip): Partial<WorkflowsIndexParams> {
  const outcome = HISTORY_CHIP_OUTCOME[chip];
  return outcome ? { outcome } : {};
}

// The one SNF selection (OLIO-3471) reads as a different facility per tab: the SELECTED (winning)
// building on Awaiting (`selectedFacility`), the RECEIVING building on Active/History (`groupId`).
// Keyed on the tab a query is FOR, not the tab on screen — so each tab's count keeps its own param.
const snfParamKeyFor = (tab: Tab) => (tab === 'awaitingAdmission' ? 'selectedFacility' : 'groupId');

export default function InboundReferralsListPage() {
  const { canCreate } = useWorkflowPermissions();
  const [creating, setCreating] = useState(false);

  const tab = useInboundReferralsStore((state) => state.tab);
  const statusChip = useInboundReferralsStore((state) => state.statusChip);
  const historyChip = useInboundReferralsStore((state) => state.historyChip);
  const dateRange = useInboundReferralsStore((state) => state.dateRange);
  const filters = useInboundReferralsStore((state) => state.filters);
  const sort = useInboundReferralsStore((state) => state.sort);
  const search = useInboundReferralsStore((state) => state.search); // committed (debounced) value
  const page = useInboundReferralsStore((state) => state.page);
  const { setTab, setStatusChip, setHistoryChip, setFilters, setSort, setSearch, setPage } =
    useInboundReferralsActions();

  // What ControlledTable wants, reading the store instead of usePagination's local state — the
  // list unmounts when a referral is opened, so a page held locally is gone by the time the person
  // comes back. Nothing resets to page 1 here: the store's actions do that (stores/
  // inboundReferralsStore.ts), because an effect on this page would also fire on mount and undo
  // the page being restored. setPageSize is unused — this list has no page-size control, and
  // neither ControlledTable nor Table ever calls it.
  const pagination = useMemo(
    () => ({
      paging: { page, pageSize: PAGE_SIZE },
      setPageIndex: (index: number) => setPage(index + 1),
      setPageSize: () => {},
    }),
    [page, setPage]
  );

  const referralSourceFilter = useMemo(() => filters.referralSource ?? [], [filters.referralSource]);
  const payerFilter = useMemo(() => filters.payer ?? [], [filters.payer]);
  const snfFilter = useMemo(() => filters.snf ?? [], [filters.snf]);
  // The quick filter's `value`, memoized so its reference is stable while the selection is unchanged
  // and the control isn't handed a fresh array every render. FilterMultiSelect compares selections by
  // option id, so these `{ id }`-only options still match the full options it committed.
  const snfValue = useMemo(() => snfFilter.map(snfOptionFor), [snfFilter]);
  const showArchived = filters.showArchived ?? false;

  // The SNFs the current user can access (OLIO-3471) — the shared hook every facility-offering
  // surface uses, so this reads from the same groups cache rather than a private query of its own.
  // Fetched eagerly (not lazily like the dropdown's own useInfiniteGroups) because the quick filter
  // is hidden for a user who covers a single SNF, and that decision needs the list before anyone
  // opens the menu. Also backs the chip-name lookup below. The dropdown still lazy-loads its own
  // option list on menu open (snfDropdown).
  const { facilities: snfOptions } = useAllowedFacilities({ type: SKILLED_NURSING_FACILITY });
  // Hide the quick filter (not the slideout copy) for a user who covers exactly one SNF — with a
  // single building there is nothing to narrow to. Empty while the list is in flight, which reads as
  // "not a single-building user", so the filter is not hidden then shown as the list lands.
  const isSingleBuildingUser = snfOptions.length === 1;

  // The dropdown's own lazy option list (loaded on menu open via useInfiniteGroups), same SNF scope.
  const snfParams = useMemo(
    () => ({ sortBy: 'name asc', type: SKILLED_NURSING_FACILITY, accessModes: [GroupAccessMode.Allowed] }),
    []
  );

  // The range in force: the person's choice, or the default recomputed each time History is shown
  // — so "last 30 days" is measured from now, not from when the app was loaded.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveRange = useMemo(() => dateRange ?? defaultDateRange(), [dateRange, tab]);

  // What every History request shares (OLIO-3295): the terminal statuses, the Date Completed
  // range, and — unless the checkbox says otherwise — no archived rows. The list, the chip counts,
  // and the slideout's own count all build on this, so their numbers agree.
  const historyScope = useMemo<Partial<WorkflowsIndexParams>>(
    () => ({
      ...tabParams('history'),
      concludedAt: effectiveRange,
      ...(showArchived ? {} : { archived: false }),
    }),
    [effectiveRange, showArchived]
  );
  // The tab's whole scope, before search and the Referral Source filter: what the list sends, and
  // what the chip counts and the slideout count are relative to.
  const scopeParams = useMemo<Partial<WorkflowsIndexParams>>(() => {
    switch (tab) {
      case 'history':
        return { ...historyScope, ...historyChipParams(historyChip) };
      // Nothing but the tab's own predicate (OLIO-3176): no chip row, no range, no archived question
      // — a waiting referral is never canceled, so it is never archived either.
      case 'awaitingAdmission':
        return tabParams('awaitingAdmission');
      // The status chip only on Active, because that's the only tab the chip row renders on —
      // otherwise a chip chosen on Active keeps filtering another tab with nothing on screen to say
      // so or undo it. The choice survives in the store, so coming back to Active restores it.
      case 'active':
        return { ...tabParams('active'), ...chipParams(statusChip) };
    }
  }, [tab, historyScope, historyChip, statusChip]);

  // One SNF selection shared across all three tabs (OLIO-3471): only the label and the query param
  // vary by tab, not the selection. Awaiting Admission reads it as the SELECTED (winning) facility
  // — label 'Selected SNF', param 'selectedFacility'; Active and History read the same buildings as
  // the RECEIVING facility — label 'SNF', param 'groupId'. So a pick made on one tab carries to the
  // others unchanged, and switching tabs only re-labels the control and re-points the param.
  const activeSnf = useMemo(
    () => ({
      placeholder: tab === 'awaitingAdmission' ? 'Selected SNF' : 'SNF',
      param: snfParamKeyFor(tab),
    }),
    [tab]
  );

  // The SNF dropdown config, mirroring the Insights SNF_DROPDOWN: options are the SNF groups the user
  // can access, labelled by name, filtered on the group id. Shared between the bar quick filter and
  // the slideout copy so both write the one `snf` key. The placeholder is the only per-tab difference
  // (Selected SNF on Awaiting); the `name` — and so the `data-cy` — stays `snf` on every tab.
  const snfDropdown = useMemo(
    () => ({
      ...defaultFilterDropdownConfig,
      name: 'snf',
      placeholder: activeSnf.placeholder,
      getOptionLabel: (option: FilterOption) => (option.name as string) ?? '',
      getOptionValue: snfValueFor,
      getOptions: { hook: useInfiniteGroups, params: snfParams },
    }),
    [snfParams, activeSnf.placeholder]
  );

  // `name` on the attr values endpoint IS the display name — the customer-facing serializer
  // sends display_name under that key (attr_values/_attr_value.json.jbuilder) and never sends
  // `displayName`. So the option's name is both the label and the value we filter by, which is
  // what collapses several raw wordings of one hospital into one option. Same read as
  // PHYSICIAN_TEAM_DROPDOWN.
  const filterSections: FilterSection[] = useMemo(
    // No section titles: SNF, Primary Payer, and Referral Source read as one flat list, matching the
    // newer Insights slideouts (OLIO-3339). Each dropdown's own placeholder names it, so a header
    // would just repeat. sortFilterDropdowns orders them alphabetically by placeholder — the same
    // helper Portfolio, All Patients, Insights, and Discharge Calendar use — so Primary Payer, then
    // Referral Source, then SNF, without hand-ordering. The SNF dropdown (OLIO-3471) has no None
    // option (every referral has a facility) and is the same control the bar quick filter renders.
    () => [
      {
        // Cast: sortFilterDropdowns returns the loose `{ placeholder; [key]: any }[]` shape (it only
        // reads placeholder), so restore the section's own filter type after sorting.
        filters: sortFilterDropdowns([
          snfDropdown,
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'payer',
            placeholder: 'Primary Payer',
            getOptionLabel: (option) => option.name ?? '',
            getOptionValue: (option) => filterValueFor(option),
            // Same shape as the Insights Payer filter (PAYER_SOURCE_DROPDOWN): the client's distinct
            // resolved payer display names (EXTERNAL_PAYER_NAME, renamed from 'payer' in OLIO-3326),
            // so the filter, the list Primary Payer column, and Insights all read one value (OLIO-3416).
            getOptions: {
              hook: useInfiniteAttrValues,
              params: { workflowValueName: VALUE_NAMES.EXTERNAL_PAYER_NAME, distinctDisplayName: true, active: true },
              // Benefit Verification isn't required before the outcome step, so a closed referral can
              // have no resolved payer — a real, common state, especially on History. None returns it.
              optionsToPrepend: [NONE_OPTION],
            },
          },
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'referralSource',
            placeholder: 'Referral Source',
            getOptionLabel: (option) => option.name ?? '',
            getOptionValue: (option) => filterValueFor(option),
            getOptions: {
              hook: useInfiniteAttrValues,
              params: { workflowValueName: VALUE_NAMES.REFERRAL_SOURCE, distinctDisplayName: true, active: true },
              // The rows with no resolved source are the ones someone has to go and resolve, and
              // without this they were the only rows no filter could reach (AC7, QA Sep 2). Same
              // NONE_OPTION every other filter prepends, so it sends the app's `null` sentinel
              // rather than a name the api would look for among the hospitals.
              optionsToPrepend: [NONE_OPTION],
            },
          },
        ]) as FilterSection['filters'],
      },
      // History only (OLIO-3295): archived referrals are hidden until asked for. On Active the
      // question cannot arise — an archived run is canceled, so it is never on that tab.
      ...(tab === 'history'
        ? [
            {
              filters: [{ filterType: FILTER_TYPES.CHECKBOX, name: 'showArchived', label: 'Show Archived Referrals' }],
            },
          ]
        : []),
    ],
    [tab, snfDropdown]
  );

  // FilterBar works in the app-standard filter shape (a record of FilterValueArrays, plus the
  // search term); the store holds the names it applied. Converting at this boundary keeps the
  // query params plain strings and the header on the shared components.
  // The archived checkbox only rides along on History: it is a History filter, and FilterBar counts
  // a ticked FilterValueBoolean in its "Filters (N)" badge, which would otherwise point Active at a
  // control that tab neither shows nor applies.
  // The store holds SNF ids, but the chip row draws each chip off its `name`. Re-label a chosen id
  // from the fetched SNF list (the same list the dropdown labels off), so "SNF: 2" expands to the
  // two building names rather than two blank chips. Falls back to the id if the list is still in
  // flight or no longer carries it.
  const snfNameById = useMemo(
    () => new Map(snfOptions.map((group) => [group.id as string, group.name as string])),
    [snfOptions]
  );

  const barFilters = useMemo(
    () => ({
      snf: new FilterValueArray(snfFilter.map((id) => ({ id, name: snfNameById.get(id) ?? id }) as FilterOption)),
      referralSource: new FilterValueArray(referralSourceFilter.map(filterOptionFor)),
      payer: new FilterValueArray(payerFilter.map(filterOptionFor)),
      ...(tab === 'history'
        ? { showArchived: new FilterValueBoolean({ name: 'showArchived', value: showArchived }) }
        : {}),
      search,
    }),
    [snfFilter, snfNameById, referralSourceFilter, payerFilter, showArchived, search, tab]
  );

  // The checkbox as FilterBar hands it back — a FilterValueBoolean, or nothing when the section
  // was not on screen (Active), which reads as "leave it as it was".
  const appliedShowArchived = (applied: BarFilters) =>
    applied.showArchived instanceof FilterValueBoolean ? applied.showArchived.value : undefined;

  // One handler for both ways FilterBar applies: a debounced search keystroke and the slideout's
  // Apply. Each sends the whole set, so neither drops what the other holds.
  const applyFilters = (applied: BarFilters) => {
    setSearch((applied.search as string) ?? '');
    const chosen = (applied.referralSource ?? []) as FilterOption[];
    const chosenPayer = (applied.payer ?? []) as FilterOption[];
    const chosenSnf = (applied.snf ?? []) as FilterOption[];
    const archived = appliedShowArchived(applied);
    setFilters((prev) => ({
      ...prev,
      referralSource: chosen.map(filterValueFor).filter(Boolean),
      payer: chosenPayer.map(filterValueFor).filter(Boolean),
      snf: chosenSnf.map(snfValueFor).filter(Boolean),
      ...(archived === undefined ? {} : { showArchived: archived }),
    }));
  };

  const portalName = usePortalName();
  // Portal column removed for now (OLIO-2758's shared list isn't live here yet — this page only
  // ever fetches referral_intake rows) — filtered out here rather than in the column factory
  // itself, which stays intact for whenever that shared list ships.
  const columns = useMemo(
    () =>
      inboundReferralsTableColumns(portalName, { tab })
        .filter((column) => column.id !== 'portal')
        // History is every terminal run, so "Current Status" (in progress vs. needing you) reads
        // oddly next to a decided referral — the column says what happened instead.
        .map((column) =>
          column.id === 'currentStatus' && tab === 'history' ? { ...column, header: 'Referral Outcome' } : column
        ),
    [portalName, tab]
  );

  // The count the filter slideout shows above Apply ("N referrals found"), so a coordinator can
  // see what a selection would leave before committing to it. Scoped exactly like the list, except
  // the referral sources and search come from what they are ticking right now rather than what is
  // applied. page_size 1 because only meta.totalRecords is wanted — the same trick the chip badges
  // use, and the reason the default parseCount already fits.
  const countMatching = (applied: BarFilters, appliedSearch: string, signal: AbortSignal) => {
    const chosen = ((applied?.referralSource ?? []) as FilterOption[]).map(filterValueFor).filter(Boolean);
    const chosenPayer = ((applied?.payer ?? []) as FilterOption[]).map(filterValueFor).filter(Boolean);
    // The one SNF selection, sent under the tab's param — Awaiting sends `selected_facility`,
    // Active/History send `group_id`.
    const chosenSnf = ((applied?.snf ?? []) as FilterOption[]).map(snfValueFor).filter(Boolean);
    // The checkbox being ticked right now, the same way the sources are read here — so the count
    // above Apply already says what toggling archived would do, in either direction.
    const archived = appliedShowArchived(applied) ?? showArchived;

    return indexWorkflows(
      {
        apiName: REFERRAL_INTAKE_API_NAME,
        ...scopeParams,
        ...(tab === 'history' ? { archived: archived ? undefined : false } : {}),
        ...(appliedSearch ? { search: appliedSearch } : {}),
        ...(chosen.length > 0 ? { referralSource: chosen } : {}),
        ...(chosenPayer.length > 0 ? { payer: chosenPayer } : {}),
        ...(chosenSnf.length > 0 ? { [activeSnf.param]: chosenSnf } : {}),
        page: 1,
        pageSize: 1,
      },
      signal
    );
  };

  // What the person has narrowed the list to, independent of which tab or chip is showing: the
  // committed search and the applied Referral Source selection. The tab labels and the chip badges
  // carry it as well as the list, so every count on the page counts the set the chips above them
  // describe — which is what lets the page do without a separate "N referrals found" line.
  const filterParams = useMemo<Partial<WorkflowsIndexParams>>(
    () => ({
      ...(search ? { search } : {}),
      ...(referralSourceFilter.length > 0 ? { referralSource: referralSourceFilter } : {}),
      ...(payerFilter.length > 0 ? { payer: payerFilter } : {}),
    }),
    [search, referralSourceFilter, payerFilter]
  );

  // Threaded into each count below under its own tab's key (see snfParamKeyFor). Kept out of
  // filterParams on purpose: filterParams is tab-invariant, but the SNF param is not — folding it in
  // would make the Active and Awaiting numbers swap params as you move between tabs.
  const snfParamsFor = (forTab: Tab): Partial<WorkflowsIndexParams> =>
    snfFilter.length > 0 ? { [snfParamKeyFor(forTab)]: snfFilter } : {};

  const queryParams: WorkflowsIndexParams = useMemo(
    () => ({
      apiName: REFERRAL_INTAKE_API_NAME,
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      ...scopeParams,
      ...filterParams,
      ...snfParamsFor(tab),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagination.paging, sort, scopeParams, filterParams, tab, snfFilter]
  );

  const { workflows, meta, isLoading, isError } = useWorkflows(queryParams);

  // A restored page can outrun the list it belongs to — referrals conclude and leave the Active
  // tab while the person is off reading one, so the page they came from may no longer exist.
  // Without this they land on an empty table with nothing on screen saying why.
  useEffect(() => {
    if (!meta) return;
    const lastPage = Math.max(meta.totalPages, 1);
    if (page > lastPage) setPage(lastPage);
  }, [meta, page, setPage]);
  // Taking a chip off is the same edit as unticking it in the slideout. `id` is what the store
  // holds — for Referral Source a reporting name (or the `null` sentinel for the unresolved rows),
  // for SNF the group id — because that is what barFilters built each chip's option from.
  //
  // Only the keys listed here are removable: `filterKey` is a plain string, so a filter added to
  // filterSections later without an entry here gets a chip whose X does nothing, rather than one
  // that quietly rewrites another selection.
  const ARRAY_FILTER_KEYS = ['referralSource', 'payer', 'snf'] as const;
  const handleRemoveFilter = (filterKey: string, id: string) =>
    setFilters((prev) => {
      if (filterKey === 'showArchived') return { ...prev, showArchived: false };
      const arrayKey = ARRAY_FILTER_KEYS.find((key) => key === filterKey);
      if (arrayKey) {
        return { ...prev, [arrayKey]: (prev[arrayKey] ?? []).filter((value) => value !== id) };
      }
      return prev;
    });

  // Chip badges (Active tab only): each is its own page_size:1 count query rather than a
  // client-side tally, because pagination means the fetched page is never the full active-tab
  // set to tally over. The Active tab label's count doubles as the "All" chip's count (same
  // params — active, no chip filter), so there's nothing extra to fetch for that one.
  const activeCount = useWorkflowCount({
    apiName: REFERRAL_INTAKE_API_NAME,
    ...tabParams('active'),
    ...filterParams,
    ...snfParamsFor('active'),
  });
  // The Awaiting Admission badge (OLIO-3176): always fetched, like Active's, because the label shows
  // on every tab.
  const awaitingCount = useWorkflowCount({
    apiName: REFERRAL_INTAKE_API_NAME,
    ...tabParams('awaitingAdmission'),
    ...filterParams,
    ...snfParamsFor('awaitingAdmission'),
  });
  const actionNeededCount = useWorkflowCount(
    {
      apiName: REFERRAL_INTAKE_API_NAME,
      ...tabParams('active'),
      ...chipParams('actionNeeded'),
      ...filterParams,
      ...snfParamsFor('active'),
    },
    tab === 'active'
  );
  const readyForReviewCount = useWorkflowCount(
    {
      apiName: REFERRAL_INTAKE_API_NAME,
      ...tabParams('active'),
      ...chipParams('readyForReview'),
      ...filterParams,
      ...snfParamsFor('active'),
    },
    tab === 'active'
  );
  // History's chips (OLIO-3295), each inside the same date range and archived setting as the list,
  // so a chip's number is what clicking it shows. Only fetched while History is on screen.
  const useHistoryCount = (chip: HistoryChip) =>
    useWorkflowCount(
      {
        apiName: REFERRAL_INTAKE_API_NAME,
        ...historyScope,
        ...historyChipParams(chip),
        ...filterParams,
        ...snfParamsFor('history'),
      },
      tab === 'history'
    );
  const historyAllCount = useHistoryCount('all');
  const selectedCount = useHistoryCount('selected');
  const notSelectedCount = useHistoryCount('notSelected');
  const deniedCount = useHistoryCount('denied');

  // Manual entry is a create, so a reader without the grant does not get the button (OLIO-2615).
  const createButton = canCreate ? (
    <CreateButton variant='outline-primary' onClick={() => setCreating(true)} data-testid='new-referral'>
      Create New Referral
    </CreateButton>
  ) : null;

  const tabOptions: TabType[] = [
    { label: `Active Referrals (${activeCount})`, value: 'active', 'data-cy': 'tab-active' },
    { label: `Awaiting Admission (${awaitingCount})`, value: 'awaitingAdmission', 'data-cy': 'tab-awaiting-admission' },
    { label: 'History', value: 'history', 'data-cy': 'tab-history' },
  ];
  const tabsRow = (
    <Tabs
      tabs={tabOptions}
      selectedTab={tabOptions.find((t) => t.value === tab)}
      onTabClick={(t) => setTab(t.value as Tab)}
    />
  );

  return (
    <Page>
      {creating && <ReferralModal onClose={() => setCreating(false)} />}
      {/* The app-standard header: FilterBar owns the title row, the search box, the `Filters (N)`
          link, Clear Filters, and the filter slideout, on desktop and mobile both, which is why
          this page no longer rolls its own. The tab strip rides in children so the tabs sit flush
          against the header's bottom border. Same shape as TasksPage.

          No Status dropdown in the quickFilter slot: OLIO-3172 removed it, because it and the chip
          row both said "Action Needed" while sending different params, and the rows that came back
          did not match the pill. The chips are the only status control now. */}
      <FilterBar
        title='Inbound Referrals'
        filters={barFilters}
        filterSections={filterSections}
        applyFilters={applyFilters}
        onClearFilters={() =>
          setFilters((prev) => ({ ...prev, referralSource: [], payer: [], snf: [], showArchived: false }))
        }
        resultsText='referrals'
        queryFn={(input: { filters: BarFilters; search?: string }, signal: AbortSignal) =>
          countMatching(input.filters, input.search ?? '', signal)
        }
        searchPlaceholder='Search Patient'
        // The SNF quick filter (OLIO-3471) — the same dropdown the slideout renders, reading and
        // writing the one `snf` store key, so a pick in either surfaces in the other. The label reads
        // 'Selected SNF' on Awaiting (snfDropdown's tab-varying placeholder), but it is the same
        // selection on every tab. Hidden for a user who covers a single SNF (the slideout copy stays).
        quickFilter={
          isSingleBuildingUser ? undefined : (
            <FilterMultiSelect
              {...snfDropdown}
              value={snfValue}
              // Debounce the commit like the other bar quick filters (Portfolio, Discharge Calendar):
              // every change fires a whole round of workflows requests (the list plus each chip/tab
              // count), so rapid picks would otherwise fire a round per pick. Batches them into one.
              debounceTime={500}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, snf: (val as FilterOption[]).map(snfValueFor).filter(Boolean) }))
              }
            />
          )
        }
        // The receiving-SNF quick filter shows at the same desktop width as this page's Create
        // button (900), rather than FilterBar's 1200 default, so it is on screen at normal widths.
        quickFilterShowMinWidth={900}
        // History has nothing to create; its header slot holds the Date Completed range instead
        // (OLIO-3295), where the outbound Active Referrals page keeps the same picker pair. A
        // hidden Create button is harmless; a hidden filter is not — the range always shows.
        callToAction={tab === 'history' ? <HistoryDateRange value={effectiveRange} /> : createButton}
        callToActionShowMinWidth={tab === 'history' ? 0 : 900}>
        <TabsRow>{tabsRow}</TabsRow>
      </FilterBar>
      {/* The applied filters as removable chips, above the chip row and the legend so
          what was filtered reads before how the results are split up. Desktop only, like the other
          lists — the chips need the width, and the slideout is the way in on mobile.

          No "N referrals found" line to go with them, though every other list page has one: the tab
          labels and the chip badges already count this list, and each of them counts within the
          applied filter, so a third number would only restate one of those two. */}
      <StyledPageSubHeader data-testid='filterSummary'>
        <DesktopOnly>
          <FilterChips filters={barFilters} onRemoveFilter={handleRemoveFilter} useStore={useInboundReferralsStore} />
        </DesktopOnly>
      </StyledPageSubHeader>
      <Page.Content>
        {tab === 'active' && (
          <Controls>
            <SegmentedControl
              options={[
                { label: `All (${activeCount})`, value: 'all', testId: 'chip-all' },
                { label: `Flagged (${actionNeededCount})`, value: 'actionNeeded', testId: 'chip-action-needed' },
                { label: `Ready for Review (${readyForReviewCount})`, value: 'readyForReview', testId: 'chip-ready' },
              ]}
              selectedValue={statusChip}
              onChange={(value) => setStatusChip(value as StatusChip)}
            />
            <Legend>
              {LEGEND.map((status) => (
                <LegendItem key={status}>
                  <Swatch $color={TONE_STYLES[LIST_STATUS_DISPLAY[status].tone].accent} />
                  {LIST_STATUS_DISPLAY[status].legendLabel ?? LIST_STATUS_DISPLAY[status].label}
                </LegendItem>
              ))}
            </Legend>
          </Controls>
        )}
        {tab === 'history' && (
          <Controls>
            <SegmentedControl
              options={[
                { label: `All (${historyAllCount})`, value: 'all', testId: 'chip-history-all' },
                { label: `Selected (${selectedCount})`, value: 'selected', testId: 'chip-selected' },
                { label: `Not Selected (${notSelectedCount})`, value: 'notSelected', testId: 'chip-not-selected' },
                { label: `Denied (${deniedCount})`, value: 'denied', testId: 'chip-denied' },
              ]}
              selectedValue={historyChip}
              onChange={(value) => setHistoryChip(value as HistoryChip)}
            />
          </Controls>
        )}

        {isError ? (
          <Body>We couldn’t load referrals.</Body>
        ) : (
          <ControlledTable<Workflow>
            data={workflows}
            columns={columns}
            loading={isLoading}
            pageCount={meta?.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            // Without this, react-table falls back to row position within the page — a
            // background refetch that reorders rows would then remap a given position to a
            // different workflow, and any per-row local state (e.g. the SNF slideout's open
            // flag) would follow the position instead of the workflow it belongs to.
            getRowId={(row) => row.id}
          />
        )}
      </Page.Content>
    </Page>
  );
}

// The tab strip rides inside FilterBar so the tabs sit flush against the header's bottom border
// rather than floating below it. The negative side margins subtract from the rendered width, so
// the basis hands that 48px back or the hairline stops short of the right padding edge.
// (Same treatment as TasksPage.)
// FilterBar renders children in both its desktop and mobile branches, and the two want different
// treatment. On desktop the strip bleeds across the bar's 24px padding so the hairline runs the
// full width — the basis hands that 48px back or it stops short of the right edge. On mobile it
// sits in MobilePageHeader's centered column, where those negative margins leave the hairline
// floating inset; there it just fills the column and pulls flush to the header's bottom edge.
const TabsRow = styled.div`
  width: 100%;
  margin-bottom: -24px;

  @media ${({ theme }) => theme.devices.desktop} {
    flex: 0 0 calc(100% + 48px);
    margin: 0 -24px -24px;
    padding: 0 24px;
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }
`;

// Match the 38px height of the shared Search pill so the two controls are the same height.
const CreateButton = styled(Button)`
  height: 38px;
  white-space: nowrap;
`;

const StyledPageSubHeader = styled(Page.SubHeader)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${({ theme }) => theme.devices.desktop} {
    gap: 18px;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const Legend = styled.div`
  display: flex;
  gap: 24px;
`;

const LegendItem = styled(BodySmall)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.black50};
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;
